import { GeminiClient } from "./base/GeminiClient";
import { PromptBuilders } from "./utils/PromptBuilders";
import { ResponseParsers } from "./utils/ResponseParsers";
import type {
  GenerateUserStoriesRequest,
  GenerateUserStoriesResponse,
} from "@/features/Projects";

export class UserStoriesService extends GeminiClient {
  async generateUserStories(
    request: GenerateUserStoriesRequest
  ): Promise<GenerateUserStoriesResponse> {
    if (!request.projectDescription?.trim()) {
      throw new Error("Descrição do projeto é obrigatória");
    }

    if (!request.objectives || request.objectives.length === 0) {
      throw new Error("Objetivos do projeto são obrigatórios");
    }

    // Para user stories, se há muitos objetivos ou áreas de foco, pode valer a pena usar batches
    const shouldUseBatches = (request.focusAreas?.length || 0) > 5 || request.objectives.length > 8;
    
    try {
      if (shouldUseBatches) {
        return await this.generateUserStoriesInBatches(request);
      } else {
        return await this.generateUserStoriesComplete(request);
      }
    } catch (error) {
      console.error("Erro ao gerar user stories:", error);
      throw new Error("Falha ao processar user stories com IA");
    }
  }

  private async generateUserStoriesComplete(
    request: GenerateUserStoriesRequest
  ): Promise<GenerateUserStoriesResponse> {
    const prompt = PromptBuilders.buildUserStoriesPrompt(
      request.projectDescription,
      request.objectives,
      request.technicalSolution?.description,
      request.timeline?.milestones
    );
    
    const response = await this.callGeminiAPI(prompt);
    
    return ResponseParsers.parseUserStoriesResponse(response);
  }

  private async generateUserStoriesInBatches(
    request: GenerateUserStoriesRequest
  ): Promise<GenerateUserStoriesResponse> {
    
    const allEpics: any[] = [];
    const allBatches: any[] = [];
    const batchSize = 4; // 4 objetivos por batch
    
    for (let i = 0; i < request.objectives.length; i += batchSize) {
      const objectiveBatch = request.objectives.slice(i, i + batchSize);
      const batchNumber = Math.floor(i / batchSize) + 1;
      const totalBatches = Math.ceil(request.objectives.length / batchSize);
      
      
      try {
        const batchPrompt = PromptBuilders.buildUserStoriesPrompt(
          request.projectDescription,
          objectiveBatch,
          request.technicalSolution?.description,
          request.timeline?.milestones
        );
        
        const response = await this.callGeminiAPI(batchPrompt);
        const batchResult = ResponseParsers.parseUserStoriesResponse(response);
        
        // 🔍 Armazenar resultado completo do batch
        allBatches.push(batchResult);
        allEpics.push(...batchResult.stories.epics);
        
        
        // Pausa entre batches
        if (i + batchSize < request.objectives.length) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      } catch (error) {
        console.error(`❌ Erro no batch ${batchNumber}:`, error);
      }
    }
    
    // 🔍 CORREÇÃO: Consolidar objetos completos das stories
    
    // Coletar stories completas de cada batch
    const allStoriesObjects: any[] = [];
    for (let i = 0; i < allBatches.length; i++) {
      const batchResult = allBatches[i];
      if (batchResult?.stories?.stories) {
        allStoriesObjects.push(...batchResult.stories.stories);
      }
    }
    
    
    const currentTime = new Date().toISOString();
    
    
    return {
      stories: {
        epics: allEpics,
        stories: allStoriesObjects,
        totalStoryPoints: allStoriesObjects.reduce((sum: number, story: any) => sum + (story.storyPoints || 0), 0),
        totalEstimatedHours: allStoriesObjects.reduce((sum: number, story: any) => sum + (story.estimatedHours || 0), 0),
        createdAt: currentTime,
        updatedAt: currentTime,
      }
    };
  }
}